import React, { useState, useEffect, useCallback, useRef } from 'react';
import { 
  FileText, Search, Upload, Check, X, User, 
  Download, Trash2, Eye, Plus, File
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Label } from '../components/ui/label';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { api } from '../lib/api';
import { toast } from 'sonner';
import PINGate from '../components/PINGate';

// Document types configuration (updated)
const DOCUMENT_TYPES = [
  { id: 'joining_form', name: 'Employee Joining Form', icon: '📋' },
  { id: 'experience_relieving_letter', name: 'Experience/Relieving Letter', icon: '📄' },
  { id: 'salary_slips_last_3_months', name: 'Salary Slips (Last 3 Months)', icon: '💰' },
  { id: 'pan_card', name: 'Full PAN Card', icon: '💳' },
  { id: 'aadhar_card', name: 'Full Aadhar Card', icon: '🪪' },
  { id: 'driving_license', name: 'Full Driving License', icon: '🚗' },
  { id: 'voter_id', name: 'Full Voter ID', icon: '🗳️' },
  { id: 'passport', name: 'Passport', icon: '🛂' },
  { id: 'bank_passbook_photo', name: 'Bank Passbook Photo', icon: '🏦' },
  { id: 'generated_offer_letter', name: 'Generated Offer Letter', icon: '📝' },
  { id: 'signed_offer_letter', name: 'Signed Offer Letter', icon: '✍️' }
];

const HRDocuments = () => {
  const [employees, setEmployees] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState('');
  const searchTimeoutRef = useRef(null);
  const [selectedEmployee, setSelectedEmployee] = useState(null);
  const [documents, setDocuments] = useState([]);
  const [showDocModal, setShowDocModal] = useState(false);
  const [showAddDocModal, setShowAddDocModal] = useState(false);
  const [pinVerified, setPinVerified] = useState(false);
  const [saving, setSaving] = useState(false);

  // New document form
  const [newDoc, setNewDoc] = useState({
    document_type: '',
    document_name: '',
    document_url: '',
    notes: ''
  });

  useEffect(() => {
    if (pinVerified) {
      fetchEmployees();
    }
  }, [pinVerified]);

  // Debounced search
  const handleSearchChange = useCallback((value) => {
    setSearchTerm(value);
    
    if (searchTimeoutRef.current) {
      clearTimeout(searchTimeoutRef.current);
    }
    
    searchTimeoutRef.current = setTimeout(() => {
      setDebouncedSearchTerm(value);
    }, 300);
  }, []);

  const fetchEmployees = async () => {
    try {
      const response = await api.getHREmployees();
      setEmployees(response.data);
    } catch (error) {
      toast.error('Failed to load employees');
    } finally {
      setLoading(false);
    }
  };

  const fetchEmployeeDocuments = async (employee) => {
    try {
      const response = await api.getEmployeeDocuments(employee.id);
      setDocuments(response.data);
    } catch (error) {
      toast.error('Failed to load documents');
    }
  };

  const openDocModal = async (employee) => {
    setSelectedEmployee(employee);
    await fetchEmployeeDocuments(employee);
    setShowDocModal(true);
  };

  const openAddDocModal = (docType = '') => {
    setNewDoc({
      document_type: docType,
      document_name: docType ? DOCUMENT_TYPES.find(d => d.id === docType)?.name || '' : '',
      document_url: '',
      notes: ''
    });
    setShowAddDocModal(true);
  };

  const handleAddDocument = async () => {
    if (!newDoc.document_type) {
      toast.error('Please select a document type');
      return;
    }
    if (!newDoc.document_name) {
      toast.error('Please enter a document name');
      return;
    }

    setSaving(true);
    try {
      await api.addEmployeeDocument({
        employee_id: selectedEmployee.id,
        ...newDoc
      });
      toast.success('Document added successfully');
      setShowAddDocModal(false);
      await fetchEmployeeDocuments(selectedEmployee);
      fetchEmployees(); // Refresh to update document count
    } catch (error) {
      toast.error('Failed to add document');
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteDocument = async (docId) => {
    if (!window.confirm('Are you sure you want to delete this document?')) return;

    try {
      await api.deleteEmployeeDocument(docId);
      toast.success('Document deleted');
      await fetchEmployeeDocuments(selectedEmployee);
      fetchEmployees();
    } catch (error) {
      toast.error('Failed to delete document');
    }
  };

  const filteredEmployees = employees.filter(emp => 
    emp.name?.toLowerCase().includes(debouncedSearchTerm.toLowerCase()) ||
    emp.email?.toLowerCase().includes(debouncedSearchTerm.toLowerCase())
  );

  const getDocumentStatus = (employee) => {
    const count = employee.document_count || 0;
    const total = DOCUMENT_TYPES.length;
    
    if (count === 0) return { label: 'No Documents', color: 'bg-red-100 text-red-800' };
    if (count < total) return { label: `${count}/${total} Documents`, color: 'bg-amber-100 text-amber-800' };
    return { label: 'Complete', color: 'bg-green-100 text-green-800' };
  };

  const DocumentsContent = () => {
    if (loading) {
      return (
        <div className="flex items-center justify-center min-h-[400px]">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600"></div>
        </div>
      );
    }

    return (
      <div className="space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-slate-900">Employee Documents</h1>
            <p className="text-slate-500">Manage employee documentation</p>
          </div>
        </div>

        {/* Document Types Legend */}
        <Card>
          <CardContent className="p-4">
            <div className="flex flex-wrap items-center gap-3">
              <span className="text-sm font-medium text-slate-600">Required Documents:</span>
              {DOCUMENT_TYPES.map(doc => (
                <Badge key={doc.id} variant="outline" className="text-xs">
                  {doc.icon} {doc.name}
                </Badge>
              ))}
            </div>
          </CardContent>
        </Card>

        {/* Search */}
        <Card>
          <CardContent className="p-4">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
              <Input
                placeholder="Search employees..."
                value={searchTerm}
                onChange={(e) => handleSearchChange(e.target.value)}
                className="pl-10"
              />
            </div>
          </CardContent>
        </Card>

        {/* Employees List */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <FileText className="h-5 w-5 text-slate-500" />
              Employee Documents Status
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="divide-y divide-slate-100">
              {filteredEmployees.map((employee) => {
                const status = getDocumentStatus(employee);
                return (
                  <div 
                    key={employee.id}
                    className="py-4 flex items-center justify-between hover:bg-slate-50 px-4 -mx-4 cursor-pointer transition-colors"
                    onClick={() => openDocModal(employee)}
                  >
                    <div className="flex items-center gap-4">
                      <div className="w-12 h-12 rounded-full bg-indigo-100 flex items-center justify-center">
                        <User className="h-6 w-6 text-indigo-600" />
                      </div>
                      <div>
                        <h3 className="font-semibold text-slate-900">{employee.name}</h3>
                        <p className="text-sm text-slate-500">{employee.email}</p>
                        <p className="text-xs text-slate-400 capitalize">{employee.role?.replace(/_/g, ' ')}</p>
                      </div>
                    </div>
                    <div className="flex items-center gap-4">
                      <Badge className={status.color}>
                        {status.label}
                      </Badge>
                      <Button size="sm" variant="outline">
                        <Eye className="h-4 w-4 mr-1" />
                        View
                      </Button>
                    </div>
                  </div>
                );
              })}
              {filteredEmployees.length === 0 && (
                <div className="py-12 text-center text-slate-500">
                  No employees found
                </div>
              )}
            </div>
          </CardContent>
        </Card>

        {/* Documents Modal */}
        <Dialog open={showDocModal} onOpenChange={setShowDocModal}>
          <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-full bg-indigo-100 flex items-center justify-center">
                  <User className="h-5 w-5 text-indigo-600" />
                </div>
                <div>
                  <span className="block">{selectedEmployee?.name}</span>
                  <span className="text-sm font-normal text-slate-500">{selectedEmployee?.email}</span>
                </div>
              </DialogTitle>
            </DialogHeader>

            <div className="space-y-4">
              {/* Document Grid */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                {DOCUMENT_TYPES.map(docType => {
                  const doc = documents.find(d => d.document_type === docType.id);
                  const isUploaded = doc?.uploaded;

                  return (
                    <div 
                      key={docType.id}
                      className={`p-4 rounded-lg border-2 ${
                        isUploaded 
                          ? 'border-green-200 bg-green-50' 
                          : 'border-slate-200 bg-slate-50'
                      }`}
                    >
                      <div className="flex items-start justify-between">
                        <div className="flex items-center gap-2">
                          <span className="text-xl">{docType.icon}</span>
                          <div>
                            <p className="font-medium text-slate-900">{docType.name}</p>
                            {isUploaded ? (
                              <p className="text-xs text-green-600 flex items-center gap-1">
                                <Check className="h-3 w-3" />
                                Uploaded
                              </p>
                            ) : (
                              <p className="text-xs text-slate-500">Not uploaded</p>
                            )}
                          </div>
                        </div>
                        <div className="flex items-center gap-1">
                          {isUploaded ? (
                            <>
                              {doc.document_url && (
                                <Button 
                                  size="sm" 
                                  variant="ghost"
                                  onClick={() => window.open(doc.document_url, '_blank')}
                                >
                                  <Eye className="h-4 w-4" />
                                </Button>
                              )}
                              <Button 
                                size="sm" 
                                variant="ghost"
                                className="text-red-600 hover:text-red-700"
                                onClick={() => handleDeleteDocument(doc.id)}
                              >
                                <Trash2 className="h-4 w-4" />
                              </Button>
                            </>
                          ) : (
                            <Button 
                              size="sm" 
                              variant="outline"
                              onClick={() => openAddDocModal(docType.id)}
                            >
                              <Plus className="h-4 w-4 mr-1" />
                              Add
                            </Button>
                          )}
                        </div>
                      </div>
                      {isUploaded && doc.notes && (
                        <p className="mt-2 text-xs text-slate-600 bg-white p-2 rounded">
                          Note: {doc.notes}
                        </p>
                      )}
                    </div>
                  );
                })}
              </div>

              {/* Progress */}
              <div className="p-3 bg-slate-100 rounded-lg">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-sm font-medium text-slate-700">Document Completion</span>
                  <span className="text-sm text-slate-600">
                    {documents.filter(d => d.uploaded).length}/{DOCUMENT_TYPES.length}
                  </span>
                </div>
                <div className="w-full bg-slate-200 rounded-full h-2">
                  <div 
                    className="bg-green-500 h-2 rounded-full transition-all"
                    style={{ 
                      width: `${(documents.filter(d => d.uploaded).length / DOCUMENT_TYPES.length) * 100}%` 
                    }}
                  />
                </div>
              </div>
            </div>
          </DialogContent>
        </Dialog>

        {/* Add Document Modal */}
        <Dialog open={showAddDocModal} onOpenChange={setShowAddDocModal}>
          <DialogContent className="max-w-md max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>Add Document</DialogTitle>
            </DialogHeader>

            <div className="space-y-4">
              <div>
                <Label>Document Type *</Label>
                <Select 
                  value={newDoc.document_type} 
                  onValueChange={(v) => setNewDoc({
                    ...newDoc, 
                    document_type: v,
                    document_name: DOCUMENT_TYPES.find(d => d.id === v)?.name || ''
                  })}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Select document type" />
                  </SelectTrigger>
                  <SelectContent>
                    {DOCUMENT_TYPES.map(doc => (
                      <SelectItem key={doc.id} value={doc.id}>
                        {doc.icon} {doc.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div>
                <Label>Document Name *</Label>
                <Input 
                  value={newDoc.document_name}
                  onChange={(e) => setNewDoc({...newDoc, document_name: e.target.value})}
                  placeholder="e.g., Aadhar Card - John Doe"
                />
              </div>

              <div>
                <Label>Document URL (Optional)</Label>
                <Input 
                  value={newDoc.document_url}
                  onChange={(e) => setNewDoc({...newDoc, document_url: e.target.value})}
                  placeholder="https://drive.google.com/..."
                />
                <p className="text-xs text-slate-500 mt-1">
                  Link to Google Drive, Dropbox, or other cloud storage
                </p>
              </div>

              <div>
                <Label>Notes (Optional)</Label>
                <Input 
                  value={newDoc.notes}
                  onChange={(e) => setNewDoc({...newDoc, notes: e.target.value})}
                  placeholder="Any additional notes..."
                />
              </div>
            </div>

            <DialogFooter>
              <Button variant="outline" onClick={() => setShowAddDocModal(false)}>
                Cancel
              </Button>
              <Button onClick={handleAddDocument} disabled={saving}>
                {saving ? 'Saving...' : 'Add Document'}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    );
  };

  return (
    <PINGate module="hr" onVerified={() => setPinVerified(true)}>
      <DocumentsContent />
    </PINGate>
  );
};

export default HRDocuments;
